import { useEffect, useRef, useState } from "react";
import Alert from "components/common/Alert";
import PageLayout from ".././PageFromLayout";
import FormSection from "components/FormSection";
import {
  InputField,
  SelectField,
  PhoneNumberField,
  InputWithButtonField,
} from ".././FormFields";
import { countries, countryCodeOptions } from "../../utils/countryData";
import { validateForm } from "utils/validation";
export default function ScannerForm({
  initialData = null,
  onSubmit,
  submitLabel,
  onBack,
}) {
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const alertRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    cnp: "",
    loginCode: "",
    postalCode: "",
    country: "",
    gateNumber: "",
    status: "Active",
    phoneNumber: "",
    phoneCountryCode: "",
  });

  useEffect(() => {
    if (!initialData || !initialData._id) return;

    setFormData({
      firstName: initialData.firstName || "",
      lastName: initialData.lastName || "",
      email: initialData.email || "",
      cnp: initialData.cnp || "",
      loginCode: initialData.loginCode || "",
      postalCode: initialData.postalCode || "",
      country: initialData.country || "",
      gateNumber: initialData.gateNumber || "",
      status: initialData.status || "Active",
      phoneNumber: initialData.phoneNumber || "",
      phoneCountryCode: initialData.phoneCountryCode || "",
    });
  }, [initialData]);

  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [alert]);
  const schema = {
    firstName: { required: true, label: "First Name" },
    lastName: { required: true, label: "Last Name" },
    email: { required: true, type: "email", label: "Email" },
    cnp: { required: true, label: "CNP" },
    phoneNumber: { required: true, type: "phone", label: "Phone Number" },
    loginCode: { required: true, label: "Login Code" },
    postalCode: { required: true, label: "Zip / Postal Code" },
    country: { required: true, label: "Country" },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
  };

  // Helper to generate a random 6-digit login code
  const handleGenerateCode = () => {
    const randomCode = Math.floor(100000 + Math.random() * 900000).toString();
    setFormData((prev) => ({ ...prev, loginCode: randomCode }));
    setErrors((p) => ({ ...p, loginCode: "" }));
  };

  const submit = async (ev) => {
    if (ev) ev.preventDefault();

    const validationErrors = await validateForm(formData, schema);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setAlert("Please fix the errors below");
      return;
    }

    setErrors({});
    setAlert("");
    setSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (err) {
      console.error(err);
      setAlert("An error occurred while saving the scanner.");
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = initialData ? "Edit Scanner" : "Add Scanner";
  const pageSubTitle = initialData
    ? "Update the scanner details below."
    : "Add a new scanner to the system.";

  return (
    <PageLayout
      onBack={onBack}
      onSubmit={submit}
      submitText={submitLabel || (initialData ? "Update Scanner" : "Create")}
      loading={submitting}
    >
      <form onSubmit={submit} className="flex flex-col gap-6">
        {alert && (
          <div ref={alertRef}>
            <Alert type="error" message={alert} onClose={() => setAlert("")} />
          </div>
        )}

        <FormSection
          title="Scanner Details"
          subtitle={`${initialData ? "Update" : "Add"} the main details about the scanner.`}
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <InputField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              error={errors.firstName}
              required
            />

            <InputField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              error={errors.lastName}
              required
            />
            <InputField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              error={errors.email}
              required
            />
            <InputField
              label="CNP (Cod Numeric Personal)"
              name="cnp"
              value={formData.cnp}
              onChange={handleChange}
              placeholder="Enter CNP number"
              error={errors.cnp}
              required
            />
            <PhoneNumberField
              label="Phone Number"
              name="phoneNumber"
              codeName="phoneCountryCode"
              phoneValue={formData.phoneNumber}
              codeValue={formData.phoneCountryCode}
              onChange={handleChange}
              onCodeChange={(val) => {
                setFormData((prev) => ({
                  ...prev,
                  phoneCountryCode: val,
                }));
                setErrors((p) => ({
                  ...p,
                  phoneCountryCode: "",
                }));
              }}
              options={countryCodeOptions}
              placeholder="712 345 678"
              required
              error={errors.phoneNumber}
            />

            {/* Login Code Field with Generate Button */}
            <InputWithButtonField
              label="Login Code"
              name="loginCode"
              value={formData.loginCode}
              onChange={handleChange}
              onButtonClick={handleGenerateCode}
              buttonText="Generate"
              placeholder="Enter login code"
              disabled={true}
            />

            <InputField
              label="Zip / Postal Code"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              placeholder="Enter Zip/Postal code"
              error={errors.postalCode}
              required
            />

            <SelectField
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              options={countries}
              placeholder="Select country"
              isSearchable={true}
              error={errors.country}
              required
            />

            <InputField
              label="Gate / Location"
              name="gateNumber"
              value={formData.gateNumber}
              onChange={handleChange}
              placeholder="e.g. Block No 1"
              error={errors.gateNumber}
            />

            <SelectField
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={[
                { label: "Active", value: "Active" },
                { label: "Inactive", value: "Inactive" },
              ]}
              placeholder="Select status"
              error={errors.status}
            />
          </div>
        </FormSection>
      </form>
    </PageLayout>
  );
}
